import { useState, useEffect, useCallback, useRef } from 'react';
import { Participant, ChatMessage, Meeting } from '../types';
import { api, API_BASE } from '../lib/api';

export function useMeeting(
  publicId: string,
  isJoined: boolean,
  userId?: string,
  userName?: string,
  onSignal?: (fromParticipantId: string, signalData: any) => void,
  onParticipantJoined?: (participant: Participant) => void,
  onParticipantLeft?: (participantId: string) => void
) {
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [myParticipantId, setMyParticipantId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [activeReactions, setActiveReactions] = useState<{ id: string; participantId: string; reaction: string }[]>([]);
  const [isMeetingEnded, setIsMeetingEnded] = useState(false);
  const [wasKicked, setWasKicked] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);
  const isUnmountedRef = useRef(false);
  const intentionalCloseRef = useRef(false);
  const isMeetingEndedRef = useRef(false);
  const wasKickedRef = useRef(false);

  // Keep latest user info and callbacks accessible without triggering re-effects
  const userRef = useRef({ id: userId, name: userName });
  const onSignalRef = useRef(onSignal);
  const onJoinedRef = useRef(onParticipantJoined);
  const onLeftRef = useRef(onParticipantLeft);

  useEffect(() => {
    userRef.current = { id: userId, name: userName };
  }, [userId, userName]);

  useEffect(() => {
    onSignalRef.current = onSignal;
  }, [onSignal]);

  useEffect(() => {
    onJoinedRef.current = onParticipantJoined;
  }, [onParticipantJoined]);

  useEffect(() => {
    onLeftRef.current = onParticipantLeft;
  }, [onParticipantLeft]);

  // Load meeting metadata & chat history once per publicId
  useEffect(() => {
    if (!publicId) {
      setMeeting(null);
      setChatMessages([]);
      setParticipants([]);
      return;
    }

    let isMounted = true;
    async function load() {
      try {
        const [meetingData, history] = await Promise.all([
          api.getMeeting(publicId),
          api.getChatHistory(publicId).catch(() => []),
        ]);
        if (isMounted) {
          setMeeting(meetingData);
          setChatMessages(history);
        }
      } catch (e: any) {
        if (isMounted) {
          setConnectionError(e.message || 'Failed to load meeting details');
        }
      }
    }

    load();

    return () => {
      isMounted = false;
    };
  }, [publicId]);

  // Manage WebSocket connection lifecycle
  useEffect(() => {
    if (!publicId || !isJoined || !userId) {
      return;
    }

    isUnmountedRef.current = false;
    intentionalCloseRef.current = false;
    isMeetingEndedRef.current = false;
    wasKickedRef.current = false;
    retryCountRef.current = 0;

    const connect = () => {
      if (
        isUnmountedRef.current ||
        intentionalCloseRef.current ||
        isMeetingEndedRef.current ||
        wasKickedRef.current
      ) {
        return;
      }

      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      const token = localStorage.getItem('cfmeet_token') || '';
      const q = token ? `?token=${encodeURIComponent(token)}` : '';
      const wsUrl = `${API_BASE.replace(/^http/, 'ws')}/api/meetings/${encodeURIComponent(publicId)}/room/ws${q}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsReconnecting(false);
        setConnectionError(null);
        retryCountRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          switch (msg.type) {
            case 'initial_state':
              if (msg.participantId) {
                setMyParticipantId(msg.participantId);
              }
              if (Array.isArray(msg.participants)) {
                setParticipants(msg.participants);
              }
              break;

            case 'participant.joined':
              if (msg.participant) {
                setParticipants((prev) => {
                  const filtered = prev.filter((p) => p.id !== msg.participant.id);
                  return [...filtered, msg.participant];
                });
                onJoinedRef.current?.(msg.participant);
              }
              break;

            case 'participant.left':
              setParticipants((prev) => prev.filter((p) => p.id !== msg.participantId));
              onLeftRef.current?.(msg.participantId);
              break;

            case 'participant.state_changed':
              setParticipants((prev) =>
                prev.map((p) => {
                  if (p.id === msg.participantId) {
                    return {
                      ...p,
                      audio: msg.audio !== undefined ? msg.audio : p.audio,
                      video: msg.video !== undefined ? msg.video : p.video,
                      screen: msg.screen !== undefined ? msg.screen : p.screen,
                      handRaised: msg.handRaised !== undefined ? msg.handRaised : p.handRaised,
                      speaking: msg.speaking !== undefined ? msg.speaking : p.speaking,
                    };
                  }
                  return p;
                })
              );
              break;

            case 'signal':
              if (msg.fromParticipantId && msg.signalData) {
                onSignalRef.current?.(msg.fromParticipantId, msg.signalData);
              }
              break;

            case 'chat':
              if (msg.message) {
                setChatMessages((prev) => [...prev, msg.message]);
              }
              break;

            case 'reaction':
              const reactionId = `${msg.participantId}_${Date.now()}`;
              setActiveReactions((prev) => [
                ...prev,
                { id: reactionId, participantId: msg.participantId, reaction: msg.reaction },
              ]);
              setTimeout(() => {
                setActiveReactions((prev) => prev.filter((r) => r.id !== reactionId));
              }, 3000);
              break;

            case 'host.mute':
              window.dispatchEvent(new CustomEvent('cfmeet:host-mute'));
              break;

            case 'participant.removed':
              wasKickedRef.current = true;
              setWasKicked(true);
              intentionalCloseRef.current = true;
              ws.close(1000, 'kicked');
              break;

            case 'meeting.ended':
              isMeetingEndedRef.current = true;
              setIsMeetingEnded(true);
              intentionalCloseRef.current = true;
              ws.close(1000, 'ended');
              break;
          }
        } catch (err) {
          console.error('Error processing websocket message:', err);
        }
      };

      ws.onclose = (event) => {
        if (
          isUnmountedRef.current ||
          intentionalCloseRef.current ||
          isMeetingEndedRef.current ||
          wasKickedRef.current
        ) {
          return;
        }

        if (event.code === 1000 || event.code === 4003) {
          if (event.code === 4003) {
            wasKickedRef.current = true;
            setWasKicked(true);
          } else if (event.reason === 'Meeting ended by host' || event.reason === 'ended') {
            isMeetingEndedRef.current = true;
            setIsMeetingEnded(true);
          }
          return;
        }

        setIsReconnecting(true);
        const delay = Math.min(1000 * Math.pow(1.5, retryCountRef.current), 10000);
        retryCountRef.current++;

        reconnectTimeoutRef.current = window.setTimeout(() => {
          connect();
        }, delay);
      };

      ws.onerror = () => {
        // Handled by onclose
      };
    };

    connect();

    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000);

    return () => {
      isUnmountedRef.current = true;
      intentionalCloseRef.current = true;
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'cleanup');
        wsRef.current = null;
      }
    };
  }, [publicId, isJoined, userId]);

  const sendStateUpdate = useCallback((state: { audio?: boolean; video?: boolean; screen?: boolean; handRaised?: boolean; speaking?: boolean }) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'participant.state', ...state }));
    }
  }, []);

  const sendSignal = useCallback((targetParticipantId: string, signalData: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'signal',
        targetParticipantId,
        signalData,
      }));
    }
  }, []);

  const sendChat = useCallback((message: string) => {
    const text = message.trim();
    if (!text) return;

    const user = userRef.current;
    if (user && user.id) {
      const localMsg: ChatMessage = {
        id: `local_${Date.now()}`,
        senderId: user.id,
        senderName: user.name || 'You',
        message: text,
        timestamp: Date.now(),
      };
      setChatMessages((prev) => [...prev, localMsg]);
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'chat', message: text }));
    }
  }, []);

  const sendReaction = useCallback((reaction: string) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'reaction', reaction }));
    }
  }, []);

  const kickParticipant = useCallback(async (participantId: string) => {
    await api.removeParticipant(publicId, participantId);
  }, [publicId]);

  const muteParticipant = useCallback(async (participantId: string) => {
    await api.muteParticipant(publicId, participantId);
  }, [publicId]);

  const muteAll = useCallback(async () => {
    await api.muteAll(publicId);
  }, [publicId]);

  const endMeeting = useCallback(async () => {
    intentionalCloseRef.current = true;
    isMeetingEndedRef.current = true;
    setIsMeetingEnded(true);
    if (wsRef.current) {
      wsRef.current.close(1000, 'host ended meeting');
    }
    await api.endMeeting(publicId);
  }, [publicId]);

  return {
    meeting,
    participants,
    myParticipantId,
    chatMessages,
    isReconnecting,
    connectionError,
    activeReactions,
    isMeetingEnded,
    wasKicked,
    sendStateUpdate,
    sendSignal,
    sendChat,
    sendReaction,
    kickParticipant,
    muteParticipant,
    muteAll,
    endMeeting,
  };
}
